/**
 * Formatting helpers for cost (USD) and duration (milliseconds).
 * Used by workflow KPI tiles and metric tiles.
 */

export interface FormatUsdOptions {
  decimals?: number;
}

/**
 * Format a USD value for display.
 * - 0       → "$0"
 * - <0.001  → "<$0.001"
 * - <1      → "$0.123" (3 decimal places)
 * - ≥1      → "$1.23"  (2 decimal places unless decimals overridden)
 * - negative → "-$1.23"
 */
export function formatUsd(n: number, opts?: FormatUsdOptions): string {
  const decimals = opts?.decimals;
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);

  if (abs === 0) return "$0";
  if (abs < 0.001) return `${sign}<$0.001`;

  const d = decimals !== undefined ? decimals : abs < 1 ? 3 : 2;
  return `${sign}$${abs.toFixed(d)}`;
}

/**
 * Format a duration given in milliseconds to a human-readable string.
 * Picks the most appropriate unit based on magnitude:
 *   < 1000ms   → "850ms"
 *   < 60 000ms → "8.2s"
 *   < 3 600 000ms → "12m"
 *   ≥ 3 600 000ms → "1h 3m"  (seconds stripped at hour scale)
 */
export function formatDurationMs(ms: number): string {
  if (ms < 0) return "0ms";
  if (ms < 1_000) return `${Math.round(ms)}ms`;

  const totalSec = ms / 1_000;
  if (totalSec < 60) {
    // One decimal place for sub-minute — "8.2s"
    return `${parseFloat(totalSec.toFixed(1))}s`;
  }

  const totalMin = Math.floor(totalSec / 60);
  if (totalMin < 60) return `${totalMin}m`;

  const hours = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}
